import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { createAuthGuard } from 'keycloak-angular';
import { AppRoutes } from '../models/routes.model';
import { AppContextService } from '../services/app-context.service';
import { KeycloakInitStateService } from '../services/keycloak-init-state.service';

/**
 * Protegge le rotte applicative: all'avvio l'app non manda nessuno su Keycloak
 * (vedi keycloak.config.ts), quindi è questo guard a decidere.
 *
 * L'utente non autenticato viene mandato a Keycloak conservando la rotta
 * richiesta come `redirectUri`, così dopo il login atterra dove voleva andare
 * invece che sempre sulla dashboard.
 *
 * Se il ritorno da Keycloak è appena fallito, niente nuovo login automatico
 * (sarebbe un loop, vedi `KeycloakInitStateService`): si atterra sulla vetrina
 * e l'avviso nel layout offre il tentativo manuale.
 */
export const authGuard: CanActivateFn = createAuthGuard(
  async (_next, state, { authenticated, keycloak }) => {
    if (authenticated) {
      return true;
    }

    // `inject` va risolto prima di qualsiasi await: dopo, il contesto di
    // iniezione non è più disponibile e lancerebbe NG0203.
    const router = inject(Router);
    const initState = inject(KeycloakInitStateService);
    const appContextService = inject(AppContextService);

    if (initState.loginFailed()) {
      initState.showNotice();
      return router.parseUrl(appContextService.buildAppScopedUrl(AppRoutes.DASHBOARD));
    }

    // Il deep link richiesto sopravvive al giro su Keycloak.
    await keycloak.login({
      redirectUri: `${window.location.origin}${state.url}`,
    });

    // `login()` lascia la pagina; il fallback vale solo se il redirect non
    // parte (Keycloak non raggiungibile): meglio la schermata di login che una
    // rotta protetta a metà.
    return router.createUrlTree(['/', AppRoutes.AUTH, AppRoutes.AUTH_LOGIN]);
  }
);
